import { Controller } from "@hotwired/stimulus"
import { trackPdfRequested, trackPdfDownloaded, trackAdviserCtaClicked } from "../modules/mortgage_tools/ga4_tracker"

const REPORT_VARIANT = "schedule"
const MAX_POLL_ATTEMPTS = 30
const POLL_INTERVAL_MS = 2000
const STATUS_UPDATE_EVERY = 3
const ERROR_CLASSES = ["moneysmart-flash-alert", "mb-4"]

export default class extends Controller {
  static targets = ["button", "statusText", "errorText"]
  static values = { generateUrl: String, calculatorType: String }

  async generate(event) {
    event.preventDefault()
    if (this._polling) return
    this._polling = true

    this._clearError()
    this.element.setAttribute("aria-busy", "true")
    this._updateStatus("Preparing PDF...")
    trackPdfRequested(this.calculatorTypeValue, REPORT_VARIANT)

    try {
      const response = await fetch(this.generateUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({
          calculator_type: this.calculatorTypeValue,
          params: this._readParams()
        })
      })

      const data = await this._readJson(response)

      if (!response.ok) {
        if (response.status === 401 && data.sign_in_url) {
          window.location.href = data.sign_in_url
          return
        }
        this._showError(data.error || this._errorForStatus(response.status))
        return
      }

      if (data.status === "completed") {
        this._triggerDownload(data.download_url)
        return
      }

      if (data.status === "processing" && data.poll_url) {
        this._pollForCompletion(data.poll_url)
        return
      }

      this._showError("Unexpected response")
    } catch {
      this._showError("Network error. Please try again.")
    }
  }

  _pollForCompletion(pollUrl) {
    this._polling = true
    this._pollCount = 0

    this._pollInterval = setInterval(async () => {
      this._pollCount++
      if (this._pollCount > MAX_POLL_ATTEMPTS) {
        this._stopPolling()
        this._showError("Generation timed out. Please try again.")
        return
      }

      if (this._pollCount % STATUS_UPDATE_EVERY === 1) {
        const elapsedSeconds = Math.round((this._pollCount * POLL_INTERVAL_MS) / 1000)
        this._updateStatus(`Generating PDF... (${elapsedSeconds}s)`)
      }

      try {
        const response = await fetch(pollUrl, { headers: { Accept: "application/json" } })
        const data = await this._readJson(response)

        if (data.status === "completed") {
          this._triggerDownload(data.download_url)
        } else if (data.status === "failed") {
          this._stopPolling()
          this._showError(data.error || "Generation failed. Please try again.")
        }
      } catch {
        this._stopPolling()
        this._showError("Connection lost. Please try again.")
      }
    }, POLL_INTERVAL_MS)
  }

  _stopPolling() {
    this._polling = false
    this.element.removeAttribute("aria-busy")
    if (this._pollInterval) {
      clearInterval(this._pollInterval)
      this._pollInterval = null
    }
  }

  _triggerDownload(url) {
    this._stopPolling()
    this._updateStatus("")
    trackPdfDownloaded(this.calculatorTypeValue, REPORT_VARIANT)
    window.location.href = url
  }

  trackAdviserCta() {
    trackAdviserCtaClicked(this.calculatorTypeValue, "schedule")
  }

  _readParams() {
    const el = document.querySelector("[data-current-params]")
    if (el) {
      try {
        return JSON.parse(el.dataset.currentParams)
      } catch {
        /* fall through to empty params */
      }
    }
    return {}
  }

  async _readJson(response) {
    try {
      return await response.json()
    } catch {
      return {}
    }
  }

  _updateStatus(text) {
    if (this.hasStatusTextTarget) this.statusTextTarget.textContent = text
  }

  _showError(message) {
    this._stopPolling()
    this._updateStatus("")
    if (this.hasErrorTextTarget) {
      this.errorTextTarget.textContent = message
      this.errorTextTarget.classList.add(...ERROR_CLASSES)
    }
  }

  _clearError() {
    if (this.hasErrorTextTarget) {
      this.errorTextTarget.textContent = ""
      this.errorTextTarget.classList.remove(...ERROR_CLASSES)
    }
  }

  _errorForStatus(status) {
    switch (status) {
      case 401:
        return "Please sign in to download PDF reports."
      case 429:
        return "Too many requests. Please try again shortly."
      default:
        return "Something went wrong. Please try again."
    }
  }

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }

  disconnect() {
    this._stopPolling()
  }
}
